import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { STAGES, CHANNELS, CHANNEL_LABELS, type StageId, AGE_BANDS } from '@joker/domain'
import type { PeopleSort } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, ViewTitle, fmtDate, periodLabel } from '../ui'
import { BulkTagBar } from './TagPicker'

/* Oryx never stored a creation date, so this column is the earliest date we can
   prove rather than the day somebody signed up. Only the doubtful cases are
   marked; an unmarked date came from the account record itself. */
const CREATED_NOTE: Record<string, string> = {
  account: '',
  'first session': 'from training',
  migration: 'migrated',
  reset: 'training reset',
  unknown: '',
}
const CREATED_TITLE: Record<string, string> = {
  account: '',
  'first session': 'The account record is later than this person\'s first session, so the training date wins.',
  migration: 'The Cortex migration timestamp, not a signup.',
  reset: 'This person was teleported back to the start; the training before it is gone, so the real date is earlier.',
  unknown: '',
}

const PAGE_SIZE = 50

/* Whole units, no decimals: every real order here is a round three-figure sum,
   and cents would be noise in a column read at a glance. */
function money(cents: number, currency: string | null): string {
  if (!cents) return '—'
  /* A franc has no symbol, so it needs its code shown — the old map returned an
     empty string for it and a Swiss practice's 600 read as a bare number with no
     way to tell which currency it was. */
  const amount = Math.round(cents / 100).toLocaleString()
  if (currency === 'ILS') return `₪${amount}`
  if (currency === 'USD' || !currency) return `$${amount}`
  return `${amount} ${currency}`
}

/**
 * Everything the table is currently showing.
 *
 * Held by App rather than here, because opening a person unmounts this view and
 * local state would not survive the trip. Coming back to a different table than
 * the one you left is the bug that motivated lifting it.
 */
export interface PeopleView {
  /** Which slice of the list is on screen. Part of the view so it survives the
      trip into a patient's record and back, like the search and the sort. */
  audience: 'all' | 'paid' | 'vip'
  /** Where they are with us: everyone, those who have trained, or those who
      paid and have not started. */
  lifecycle: 'all' | 'patient' | 'customer'
  channel: string
  search: string
  sort: PeopleSort
  dir: 'asc' | 'desc'
  page: number
}

export const INITIAL_PEOPLE_VIEW: PeopleView = {
  audience: 'all',
  lifecycle: 'all',
  channel: '',
  search: '',
  sort: 'oryxId',
  dir: 'desc',
  page: 0,
}

export function People({
  stage,
  onStageChange,
  bucketId,
  ageBand,
  segment,
  onClearSegment,
  trainedWithin,
  onClearTrainedWithin,
  paidIn,
  onClearPaidIn,
  paidOnly,
  practiceId,
  funnelBucketId,
  funnelPick,
  onClearFunnel,
  onClearBucket,
  onClearAgeBand,
  onBackToSource,
  onClearPractice,
  onOpenPerson,
  view,
  onViewChange,
}: {
  stage?: StageId
  onStageChange: (stage: StageId | undefined) => void
  bucketId?: string
  ageBand?: string
  segment?: number
  onClearSegment: () => void
  trainedWithin?: 'ever' | 'm6' | 'm3' | 'm1' | 'w2' | 'w1' | 'today' | 'yesterday'
  onClearTrainedWithin: () => void
  /** A period from the revenue chart — 'YYYY-MM' or 'YYYY'. */
  paidIn?: string
  onClearPaidIn: () => void
  paidOnly?: boolean
  practiceId?: string
  /** Opened from a cohort's funnel: the cohort, and what was pressed. */
  funnelBucketId?: string
  funnelPick?: string
  onClearFunnel: () => void
  onClearBucket: () => void
  onClearAgeBand: () => void
  /** Returns to whichever screen sent you here. Absent when nothing did. */
  onBackToSource?: () => void
  onClearPractice: () => void
  onOpenPerson: (id: string) => void
  view: PeopleView
  onViewChange: (view: PeopleView) => void
}) {
  const { audience, channel, search, dir, page } = view
  /* Opened from a funnel, the list starts longest-waiting first unless a
     column has been chosen. */
  const fromFunnel = Boolean(funnelBucketId && funnelPick)
  const sort: PeopleSort = fromFunnel && view.sort === 'oryxId' ? 'waiting' : !fromFunnel && view.sort === 'waiting' ? 'oryxId' : view.sort
  const lifecycle = view.lifecycle ?? 'all'
  const trpc = useTRPC()
  /* Ticked rows, for tagging many at once. Kept by id, so paging keeps them. */
  const [picked, setPicked] = useState<string[]>([])
  const toggle = (id: string) => setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const qc = useQueryClient()
  const [selectingAll, setSelectingAll] = useState(false)
  const [selectNote, setSelectNote] = useState<string | null>(null)

  /* Page 7 of a filter you just changed is page 7 of a different question, and
     usually past the end of it, so every change to what is being asked resets
     it. Done in the setter rather than an effect: an effect would also fire on
     the way back from a person's record and undo the page being restored. */
  const setView = (patch: Partial<PeopleView>) =>
    onViewChange({ ...view, page: 0, ...patch })
  const setChannel = (v: string) => setView({ channel: v })
  const setSearch = (v: string) => setView({ search: v })
  const setPage = (next: number) => onViewChange({ ...view, page: next })

  /* First click on a new column takes the direction that makes that column
     useful: names read A-Z, but "most sessions" and "most recently paid" are
     what anyone opening a numeric column is looking for. */
  function sortBy(next: PeopleSort) {
    if (next === sort) setView({ dir: dir === 'asc' ? 'desc' : 'asc' })
    else {
      setView({
        sort: next,
        dir: next === 'name' || next === 'stage' || next === 'channel' ? 'asc' : 'desc',
      })
    }
  }

  const filter = {
    ...(stage ? { stage } : {}),
    ...(channel ? { channel: channel as (typeof CHANNELS)[number] } : {}),
    ...(search ? { search } : {}),
    ...(bucketId ? { bucketId } : {}),
    ...(ageBand ? { ageBand } : {}),
    ...(segment != null ? { segment } : {}),
    ...(trainedWithin ? { trainedWithin } : {}),
    ...(paidIn ? { paidIn } : {}),
    /* The prop comes from the Stats age chart, the toggle from this screen —
       either one alone is reason enough to show only people who have paid. */
    ...(paidOnly || audience === 'paid' ? { paidOnly: true } : {}),
    ...(audience === 'vip' ? { vipOnly: true } : {}),
    ...(practiceId ? { practiceId } : {}),
    ...(funnelBucketId && funnelPick ? { funnelBucketId, funnelPick } : {}),
  }
  const withLifecycle = { ...filter, ...(lifecycle !== 'all' ? { lifecycle } : {}) }
  /* How many each lifecycle tab holds under the current question, so the tabs
     say what is behind them before anyone presses one. */
  const countOf = (l: 'patient' | 'customer' | undefined) =>
    trpc.people.list.queryOptions({ ...filter, ...(l ? { lifecycle: l } : {}), limit: 1, cursor: 0 })
  const allCount = useQuery(countOf(undefined))
  const patientCount = useQuery(countOf('patient'))
  const customerCount = useQuery(countOf('customer'))

  const q = useQuery(
    trpc.people.list.queryOptions({ ...withLifecycle, sort, dir, limit: PAGE_SIZE, cursor: page * PAGE_SIZE }),
  )

  const buckets = useQuery(trpc.buckets.list.queryOptions())
  const bucketName = buckets.data?.find((b) => b.id === bucketId)?.name
  const practices = useQuery(trpc.optometrists.list.queryOptions())
  const practiceName = practices.data?.find((o) => o.id === practiceId)?.name
  const stageLabel = STAGES.find((s) => s.id === stage)?.label
  const bandLabel =
    ageBand === 'unknown' ? 'Age unknown' : AGE_BANDS.find((b) => b.id === ageBand)?.label
  const paidInLabel = paidIn ? periodLabel(paidIn) : undefined

  /* The export is a plain link so the browser saves the file itself. It carries
     the filter that produced the table, not the 50 rows on screen — exporting
     "the first page" of a 1,035-person stage would be a quiet lie. */
  /* Built field by field rather than by spreading the filter, because the
     filter holds booleans and a query string holds strings — casting the two
     together is how a flag silently stops being exported. */
  const params = new URLSearchParams({ sort, dir })
  if (stage) params.set('stage', stage)
  if (lifecycle !== 'all') params.set('lifecycle', lifecycle)
  if (channel) params.set('channel', channel)
  if (search) params.set('search', search)
  if (bucketId) params.set('bucketId', bucketId)
  if (practiceId) params.set('practiceId', practiceId)
  if (ageBand) params.set('ageBand', ageBand)
  if (segment != null) params.set('segment', String(segment))
  if (trainedWithin) params.set('trainedWithin', trainedWithin)
  if (paidIn) params.set('paidIn', paidIn)
  if (paidOnly || audience === 'paid') params.set('paidOnly', 'true')
  if (audience === 'vip') params.set('vipOnly', 'true')
  if (funnelBucketId && funnelPick) {
    params.set('funnelBucketId', funnelBucketId)
    params.set('funnelPick', funnelPick)
  }
  const funnelInfo = q.data?.funnel ?? null
  const label =
    (funnelInfo ? `${funnelInfo.cohortName} ${funnelInfo.label}` : undefined) ??
    practiceName ?? bucketName ?? bandLabel ?? paidInLabel ?? stageLabel
  if (label) params.set('label', label)
  const exportHref = `/api/export/people.csv?${params.toString()}`

  if (q.error) return <ErrorNote error={q.error} />

  const filterLabel =
    practiceName ??
    bucketName ??
    bandLabel ??
    (paidInLabel ? `who paid in ${paidInLabel}` : undefined) ??
    stageLabel ??
    (search ? 'matching the search' : 'all patients')

  const sortProps = { sort, dir, onSort: sortBy }

  const total = q.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const shown = q.data?.items.length ?? 0
  const firstRow = total === 0 ? 0 : page * PAGE_SIZE + 1
  const lastRow = page * PAGE_SIZE + shown

  return (
    <>
      {onBackToSource &&
        (fromFunnel || practiceId || bucketId || ageBand || segment != null || trainedWithin || paidIn) && (
        /* Arriving here is always from somewhere — a funnel, a practice, a
           bucket, a bar on the age chart, a segment on the usage table — and the
           filter chip alone gives no way back to it. */
        <button className="back" onClick={onBackToSource}>
          ←{' '}
          {fromFunnel
            ? (funnelInfo?.cohortName ?? 'Cohort')
            : practiceId
            ? 'Optometrists'
            : bucketId
              ? 'Cohort'
              : segment != null || trainedWithin
                ? 'Usage'
                : 'Stats'}
        </button>
      )}
      <ViewTitle suit="club">Users</ViewTitle>
      {fromFunnel && (
        /* What was pressed on the funnel, in words, with a way to drop it. */
        <p className="funnel-heading">
          <b>{funnelInfo ? `${funnelInfo.cohortName} · ${funnelInfo.label}` : 'From the funnel'}</b>
          {q.data && <span className="muted"> · {q.data.total.toLocaleString()} {q.data.total === 1 ? 'person' : 'people'}, longest waiting first</span>}{' '}
          <button type="button" className="linklike" onClick={onClearFunnel}>
            show everyone
          </button>
        </p>
      )}
      <p className="view-sub">
        Filter on anything. A filter worth keeping becomes a bucket — a named, counted, re-runnable
        audience that Phase 2 syncs to Klaviyo.
      </p>

      {/* Three slices of the same list rather than three screens: a VIP is an
          ordinary patient with a mark on them, and everything here — the search,
          the sorting, the CSV export — should work the same on all of them. */}
      <div className="seg seg-inline" role="tablist" aria-label="Which patients">
        {(['all', 'paid', 'vip'] as const).map((a) => (
          <button
            key={a}
            type="button"
            role="tab"
            aria-selected={audience === a}
            onClick={() => setView({ audience: a })}
          >
            {a === 'all' ? 'All' : a === 'paid' ? 'Paid' : 'VIP'}
          </button>
        ))}
      </div>{' '}
      {/* A person is a lead, then a customer once they pay, then a patient once
          they train. Leads have their own screen; these are the other two. */}
      <div className="seg seg-inline" role="tablist" aria-label="Where they are">
        {(
          [
            ['all', 'Everyone', allCount.data?.total],
            ['patient', 'Patients · trained', patientCount.data?.total],
            ['customer', 'Customers · paid, not started', customerCount.data?.total],
          ] as const
        ).map(([l, label, n]) => (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={lifecycle === l}
            onClick={() => setView({ lifecycle: l })}
          >
            {label}
            {n != null && <> · {n.toLocaleString()}</>}
          </button>
        ))}
      </div>

      {/* Summed over everyone the filter matches, not the page on screen —

          the numbers answer for the list you have asked for. */}

      {q.data && (

        <div className="tiles">

          <Tile k="Patients" v={total.toLocaleString()} d={filterLabel} />

          <Tile

            k="Paying"

            v={q.data.payingTotal.toLocaleString()}

            d={`${total === 0 ? 0 : Math.round((q.data.payingTotal / total) * 100)}% of them`}

          />

          <Tile

            k="Revenue"

            v={money(q.data.revenueUsdTotal, 'USD')}

            d="in USD, converted on the day of each payment"

          />

          <Tile

            k="Training"

            v={q.data.trainingTotal.toLocaleString()}

            d="have had at least one session"

          />

        </div>

      )}


      <Panel>
        <div className="toolbar">
          <div className="controls">
            <select
              value={stage ?? ''}
              onChange={(e) => onStageChange((e.target.value || undefined) as StageId | undefined)}
            >
              <option value="">All stages</option>
              {[...STAGES]
                .sort((a, b) => a.order - b.order)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
            </select>
            <select value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">All channels</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABELS[c]}
                </option>
              ))}
            </select>
            <input
              type="search"
              id="people-search"
              placeholder="Name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {/* The dropdown is how you change the filter; the chip is how you
                know what you are looking at and how you leave. A stage arrived
                at by clicking the funnel needs the second as much as a bucket
                does — more so, since nothing else on the screen says it. */}
            {stage && (
              <button type="button" className="tag" onClick={() => onStageChange(undefined)}>
                Stage: {stageLabel ?? '…'} <span aria-hidden="true">✕</span>
              </button>
            )}
            {bucketId && (
              <button type="button" className="tag" onClick={onClearBucket}>
                Bucket: {bucketName ?? '…'} <span aria-hidden="true">✕</span>
              </button>
            )}
            {practiceId && (
              <button type="button" className="tag" onClick={onClearPractice}>
                Practice: {practiceName ?? '…'} <span aria-hidden="true">✕</span>
              </button>
            )}
            {segment != null && (
              <button type="button" className="tag" onClick={onClearSegment}>
                Segment: {segmentLabel(segment)} <span aria-hidden="true">✕</span>
              </button>
            )}
            {trainedWithin && (
              <button type="button" className="tag" onClick={onClearTrainedWithin}>
                Trained: {TRAINED_LABEL[trainedWithin]} <span aria-hidden="true">✕</span>
              </button>
            )}
            {paidOnly && <span className="tag static">Paid only</span>}
            {ageBand && (
              <button type="button" className="tag" onClick={onClearAgeBand}>
                Age: {bandLabel ?? '…'} <span aria-hidden="true">✕</span>
              </button>
            )}
            {paidIn && (
              <button type="button" className="tag" onClick={onClearPaidIn}>
                Paid in {paidInLabel} <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>

          <div className="toolbar-end">
            {q.data && (
              <span className="count num">
                {total.toLocaleString()} {total === 1 ? 'patient' : 'patients'}
                {/* Always dollars. This used to add the raw paid amounts across
                    currencies and label the total with whichever currency sorted
                    first, so 800 USD, 3,600 ILS and the francs came out as
                    "21,047 CHF" — a number in no currency at all. It is the same
                    USD revenue the tile above shows, on the same filter. */}
                {q.data.revenueUsdTotal > 0 && (
                  <>
                    {' · '}
                    <strong>{money(q.data.revenueUsdTotal, 'USD')}</strong> revenue
                  </>
                )}
              </span>
            )}
            <a
              className="btn primary"
              href={exportHref}
              /* download is advisory here: the filename actually comes from the
                 Content-Disposition header, which the server sanitises. */
              download
              aria-disabled={total === 0}
            >
              <span aria-hidden="true">↓</span> Export CSV
            </a>
          </div>
        </div>

        {/* Every page, not just this one: the ids come from the same filter
            the table uses, so "all" means exactly the people counted above. */}
        {total > PAGE_SIZE && picked.length < total && (
          <div className="selectall">
            <button
              type="button"
              className="btn ghost"
              disabled={selectingAll}
              onClick={async () => {
                setSelectingAll(true)
                setSelectNote(null)
                try {
                  const r = await qc.fetchQuery(trpc.people.ids.queryOptions({ ...withLifecycle, limit: 1, cursor: 0 }))
                  setPicked(r.ids)
                  if (r.capped) setSelectNote(`Selected the first ${r.ids.length.toLocaleString()} of ${r.total.toLocaleString()}; narrow the list to reach the rest.`)
                } catch (e) {
                  setSelectNote(String((e as Error).message))
                } finally {
                  setSelectingAll(false)
                }
              }}
            >
              {selectingAll ? 'Selecting…' : `Select all ${total.toLocaleString()} in this list`}
            </button>
            {selectNote && <span className="muted">{selectNote}</span>}
          </div>
        )}
        <BulkTagBar selected={picked} onDone={() => { setPicked([]); setSelectNote(null) }} />

        {!q.data ? (
          <Loading what="patients" />
        ) : (
          <>
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <th className="pick">
                      <input
                        type="checkbox"
                        aria-label="Select every patient on this page"
                        checked={q.data.items.length > 0 && q.data.items.every((p) => picked.includes(p.id))}
                        onChange={(e) => {
                          const ids = q.data!.items.map((p) => p.id)
                          setPicked((s) => (e.target.checked ? [...new Set([...s, ...ids])] : s.filter((x) => !ids.includes(x))))
                        }}
                      />
                    </th>
                    <SortTh col="oryxId" label="ID" right {...sortProps} />
                    <SortTh col="name" label="Patient" {...sortProps} />
                    {fromFunnel && <SortTh col="waiting" label="Days waiting" right {...sortProps} />}
                    <SortTh col="stage" label="Stage" {...sortProps} />
                    <th>Tags</th>
                    <SortTh col="channel" label="Channel" {...sortProps} />

                    <SortTh col="paidAmount" label="Amount" right {...sortProps} />

                    <SortTh col="sessions" label="Sessions" right {...sortProps} />
                    <SortTh col="created" label="Created" right {...sortProps} />
                    <SortTh col="last" label="Last" right {...sortProps} />
                    <SortTh col="perWeek" label="Sess/wk" right {...sortProps} />
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.length === 0 && (
                    <tr>
                      <td colSpan={fromFunnel ? 14 : 13} className="muted empty">
                        Nothing matches those filters.
                      </td>
                    </tr>
                  )}
                  {q.data.items.map((p) => (
                    <tr
                      key={p.id}
                      tabIndex={0}
                      onClick={() => onOpenPerson(p.id)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenPerson(p.id)}
                    >
                      {/* Ticking must not open the record. */}
                      <td className="pick" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${p.name ?? 'patient'}`} checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                      </td>
                      <td className="r num muted">{p.oryxUserNumber ?? '—'}</td>
                      <td>
                        <strong>{p.name ?? '—'}</strong>
                        <br />
                        <span className="muted sub-line">{p.email ?? 'no email'}</span>
                      </td>
                      {fromFunnel && (
                        /* Days in the state that was pressed, and its name. */
                        <td className="r">
                          <span className="num">{p.waitingSince ? Math.floor((Date.now() - new Date(p.waitingSince).getTime()) / 86_400_000) : '—'}</span>
                          {p.waitingLabel && (
                            <>
                              <br />
                              <span className="muted sub-line">{p.waitingLabel}</span>
                            </>
                          )}
                        </td>
                      )}
                      <td>
                        <span className="pill">{STAGES.find((s) => s.id === p.stage)?.label}</span>
                      </td>
                      <td>
                        {p.tags.length > 0 ? (
                          <span className="tagcell">
                            {p.tags.map((t) => (
                              <span key={t} className="tagchip">{t}</span>
                            ))}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {CHANNEL_LABELS[p.channel]}
                        {/* What somebody typed as the campaign or referrer wins
                            over the practice: setting "Referral — Charlie H" and
                            still reading "Matt Downey" underneath makes the edit
                            look as though it did not save.

                            The practice only stands in for a missing detail when
                            the channel is the practice — otherwise 261 people
                            read as referred by whatever placeholder the import
                            filed them under, most of them a staff mailbox. No
                            referrer is better said by saying nothing. */}
                        {(p.channelDetail ?? (p.channel === 'optometrist' ? p.practiceName : null)) && (
                          <>
                            <br />
                            <span className="muted sub-line">
                              {p.channelDetail ?? p.practiceName}
                            </span>
                          </>
                        )}
                      </td>

                      {/* What we were paid, by whatever route: an order where
                          there is one, otherwise the practice's cut — an
                          optometrist bills their own patient, so no order for
                          them ever reaches Joker and this column read blank. */}
                      <td className="r num">
                        {money(p.revenueCents ?? p.paidCents, p.revenueCurrency ?? p.paidCurrency)}
                      </td>

                      {/* Sessions against the programme they were assigned, as
                          one figure: 22/39 means 22 of 39 done. A bare number is
                          somebody with no programme, where a denominator would
                          be invented. */}
                      <td className="r num">
                        {p.essentialsSessions
                          ? `${p.sessionsTotal}/${p.essentialsSessions}`
                          : p.sessionsTotal}
                        {p.essentialsClass && (
                          <>
                            <br />
                            <span className="muted sub-line">{p.essentialsClass}</span>
                          </>
                        )}
                      </td>
                      <td className="r num">
                        {fmtDate(p.createdAt)}
                        {CREATED_NOTE[p.createdSource] && (
                          <>
                            <br />
                            <span className="muted sub-line" title={CREATED_TITLE[p.createdSource]}>
                              {CREATED_NOTE[p.createdSource]}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="r num">
                        {p.daysSinceLastSession == null ? '—' : `${p.daysSinceLastSession}d`}
                      </td>
                      <td className="r num">{p.sessionsPerWeek ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <button
                type="button"
                className="btn"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                <span aria-hidden="true">←</span> Previous
              </button>

              <p className="pager-state" aria-live="polite">
                <strong>
                  Page {page + 1} of {pageCount.toLocaleString()}
                </strong>
                {total > 0 && (
                  <span className="muted">
                    {' '}
                    · {firstRow.toLocaleString()}–{lastRow.toLocaleString()} of{' '}
                    {total.toLocaleString()}
                  </span>
                )}
              </p>

              <button
                type="button"
                className="btn"
                onClick={() => setPage(page + 1)}
                disabled={q.data.nextCursor == null}
              >
                Next <span aria-hidden="true">→</span>
              </button>
            </div>
            <p className="footnote">
              Export writes every matching patient, not just this page. The file is UTF-8 CSV and
              opens directly in Excel, Numbers and Sheets. Clinical measurements are never
              included.
            </p>
          </>
        )}
      </Panel>
    </>
  )
}

/**
 * A sortable header. The button carries the label so the whole cell is one
 * target, and `aria-sort` on the th is what a screen reader announces — the
 * arrow is decorative and hidden from it.
 */
function SortTh({
  col,
  label,
  right,
  sort,
  dir,
  onSort,
}: {
  col: PeopleSort
  label: string
  right?: boolean
  sort: PeopleSort
  dir: 'asc' | 'desc'
  onSort: (col: PeopleSort) => void
}) {
  const active = sort === col
  return (
    <th
      className={right ? 'r sortable' : 'sortable'}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" onClick={() => onSort(col)}>
        {label}
        <span className="sort-arrow" aria-hidden="true">
          {active ? (dir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}
        </span>
      </button>
    </th>
  )
}

/* The same names the Usage sheet prints, so a chip never disagrees with the bar
   that set it. */
function segmentLabel(seq: number): string {
  if (seq === -1) return 'Not recorded'
  if (seq === 0) return 'No segment'
  if (seq === 9) return 'Maintenance'
  if (seq === 10) return 'Maintenance mix'
  return `Segment ${seq}`
}

/* The same wording the Usage tiles carry, so a chip never disagrees with the
   number that opened it. */
const TRAINED_LABEL: Record<string, string> = {
  ever: 'ever',
  m6: 'in the last 6 months',
  m3: 'in the last 3 months',
  m1: 'in the last month',
  w2: 'in the last 2 weeks',
  w1: 'in the last week',
  today: 'today',
  yesterday: 'yesterday',
}
